import type {
  AdminToken,
  CreateOrderResponse,
  OkResponse,
  OrderOut,
  OrderQuote,
  OrderStatusUpdateResponse,
  PaymentProofResponse,
  Product,
  PromoOut,
  PromoValidateResponse,
  PublicConfig,
  SubscriptionResponse,
} from "./types";

const getApiBase = (): string => {
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return "/api/v1";
  }
  const base = process.env.REACT_APP_API_BASE_URL || "";
  return `${base.replace(/\/$/, "")}/api/v1`;
};

interface RequestOptions extends RequestInit {
  token?: string;
  idempotencyKey?: string;
}

// Registered by AdminProvider so a 401 from any admin-authenticated call can
// clear the stored token and bounce back to the login screen, instead of
// leaving the dashboard silently showing stale/empty data with a dead token.
let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: (() => void) | null): void => {
  onUnauthorized = fn;
};

async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const { token, idempotencyKey, ...fetchOptions } = options;
  const url = `${getApiBase()}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  const res = await fetch(url, { ...fetchOptions, headers });

  if (!res.ok) {
    if (res.status === 401 && token && onUnauthorized) {
      onUnauthorized();
    }
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    const error: Error & { status?: number } = new Error(err.detail || `HTTP ${res.status}`);
    error.status = res.status;
    throw error;
  }

  // 204 No Content
  if (res.status === 204) return {} as T;
  return res.json();
}

// ─── Products ────────────────────────────────────────────────────────────────

export const getProducts = () => request<Product[]>("/products");
export const getProduct = (id: string) => request<Product>(`/products/${id}`);

export const adminCreateProduct = (token: string, data: object) =>
  request<Product>("/products", { method: "POST", body: JSON.stringify(data), token });

export const adminUpdateProduct = (token: string, id: string, data: object) =>
  request<Product>(`/products/${id}`, { method: "PATCH", body: JSON.stringify(data), token });

export const adminDeleteProduct = (token: string, id: string) =>
  request<OkResponse>(`/products/${id}`, { method: "DELETE", token });

// ─── Orders ──────────────────────────────────────────────────────────────────

export const quoteOrder = (data: object) =>
  request<OrderQuote>("/orders/quote", { method: "POST", body: JSON.stringify(data) });

export const createOrder = (data: object, idempotencyKey?: string) =>
  request<CreateOrderResponse>("/orders", { method: "POST", body: JSON.stringify(data), idempotencyKey });

export const adminListOrders = (token: string) =>
  request<OrderOut[]>("/orders/list", { token });

export const adminUpdateOrderStatus = (token: string, orderId: string, status: string) =>
  request<OrderStatusUpdateResponse>(`/orders/${orderId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
    token,
  });

export const adminGetPaymentProof = (token: string, orderId: string) =>
  request<PaymentProofResponse>(`/orders/${orderId}/payment-proof`, { token });

// ─── Config ──────────────────────────────────────────────────────────────────

export const getPublicConfig = () => request<PublicConfig>("/config");

// ─── Subscriptions ───────────────────────────────────────────────────────────

export const subscribe = (email: string, source = "website") =>
  request<SubscriptionResponse>("/subscriptions/subscribe", {
    method: "POST",
    body: JSON.stringify({ email, source }),
  });

export const unsubscribeEmail = (email: string) =>
  request<SubscriptionResponse>("/subscriptions/unsubscribe", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

// ─── Admin Auth ──────────────────────────────────────────────────────────────

export const adminLogin = (username: string, password: string) =>
  request<AdminToken>("/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });

// ─── Promos ──────────────────────────────────────────────────────────────────

export const validatePromo = (code: string, productIds: string[], customerEmail?: string) =>
  request<PromoValidateResponse>("/promos/validate", {
    method: "POST",
    body: JSON.stringify({ code, product_ids: productIds, customer_email: customerEmail }),
  });

export const adminListPromos = (token: string) =>
  request<PromoOut[]>("/promos/list", { token });

export const adminCreatePromo = (token: string, data: object) =>
  request<PromoValidateResponse>("/promos/add", { method: "POST", body: JSON.stringify(data), token });

export const adminUpdatePromo = (token: string, code: string, data: object) =>
  request<PromoOut>(`/promos/${code}`, {
    method: "PATCH",
    body: JSON.stringify(data),
    token,
  });

export const adminDeactivatePromo = (token: string, code: string) =>
  request<OkResponse>(`/promos/${code}/deactivate`, { method: "POST", token });

// ─── Admin product listing (includes inactive) ────────────────────────────────

export const adminListProducts = (token: string) =>
  request<Product[]>("/products/admin/list", { token });
